/**
 * WidgetPresetsModal Component
 * Modal for selecting from pre-built widget templates
 */

import React from 'react';
import { Modal, Card, Row, Col, Typography, Empty, Spin } from 'antd';
import {
  CalendarOutlined,
  UnorderedListOutlined,
  NumberOutlined,
  WarningOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  AppstoreOutlined,
  ScheduleOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import type { WidgetPreset } from '@/api/dashboard-widgets';
import { dashboardWidgetsApi } from '@/api/dashboard-widgets';

const { Text } = Typography;

// Icon mapping for presets
const ICON_MAP: Record<string, React.ReactNode> = {
  CalendarOutlined: <CalendarOutlined />,
  UnorderedListOutlined: <UnorderedListOutlined />,
  NumberOutlined: <NumberOutlined />,
  WarningOutlined: <WarningOutlined />,
  ClockCircleOutlined: <ClockCircleOutlined />,
  ExclamationCircleOutlined: <ExclamationCircleOutlined />,
  AppstoreOutlined: <AppstoreOutlined />,
  ScheduleOutlined: <ScheduleOutlined />,
};

interface WidgetPresetsModalProps {
  open: boolean;
  onClose: () => void;
  onSelect: (preset: WidgetPreset) => void;
  onCustom: () => void;
}

const WidgetPresetsModal: React.FC<WidgetPresetsModalProps> = ({
  open,
  onClose,
  onSelect,
  onCustom,
}) => {
  const { data: presets, isLoading } = useQuery({
    queryKey: ['widget-presets'],
    queryFn: () => dashboardWidgetsApi.listPresets(),
    enabled: open,
  });

  const getWidgetTypeIcon = (type: string) => {
    switch (type) {
      case 'list':
        return <UnorderedListOutlined />;
      case 'count':
        return <NumberOutlined />;
      case 'calendar':
        return <CalendarOutlined />;
      default:
        return <AppstoreOutlined />;
    }
  };

  return (
    <Modal
      title="Add Widget"
      open={open}
      onCancel={onClose}
      footer={null}
      width={700}
    >
      {isLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
          <Spin />
        </div>
      ) : (
        <>
          <div style={{
            fontFamily: 'var(--font-mono)',
            fontSize: 10,
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            color: 'var(--text-tertiary)',
            marginBottom: 12,
          }}>
            QUICK ADD
          </div>
          <Row gutter={[10, 10]}>
            {/* Custom widget option */}
            <Col span={8}>
              <Card
                hoverable
                size="small"
                onClick={onCustom}
                style={{
                  textAlign: 'center',
                  borderRadius: 2,
                  border: '1px dashed var(--accent-ink)',
                  background: 'color-mix(in srgb, var(--accent-ink) 4%, var(--surface-1))',
                  boxShadow: 'none',
                }}
              >
                <div style={{ fontSize: 20, color: 'var(--accent-ink)', marginBottom: 6 }}>
                  <PlusOutlined />
                </div>
                <Text strong style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: 11,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  color: 'var(--accent-ink)',
                }}>
                  Custom
                </Text>
                <div>
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    Build your own
                  </Text>
                </div>
              </Card>
            </Col>

            {/* Preset widgets */}
            {presets?.map((preset) => (
              <Col span={8} key={preset.id}>
                <Card
                  hoverable
                  size="small"
                  onClick={() => onSelect(preset)}
                  style={{
                    textAlign: 'center',
                    borderRadius: 2,
                    border: '1px solid var(--border-default)',
                    boxShadow: 'none',
                    background: 'var(--surface-1)',
                  }}
                >
                  <div style={{ fontSize: 20, color: 'var(--accent-ink)', marginBottom: 6 }}>
                    {preset.icon ? ICON_MAP[preset.icon] || getWidgetTypeIcon(preset.widget_type) : getWidgetTypeIcon(preset.widget_type)}
                  </div>
                  <Text strong style={{
                    fontFamily: 'var(--font-mono)',
                    fontSize: 11,
                    textTransform: 'uppercase',
                    letterSpacing: '0.04em',
                    color: 'var(--text-primary)',
                  }}>
                    {preset.name}
                  </Text>
                  <div>
                    <Text type="secondary" style={{ fontSize: 11 }}>
                      {preset.description || `${preset.widget_type} widget`}
                    </Text>
                  </div>
                </Card>
              </Col>
            ))}
          </Row>

          {(!presets || presets.length === 0) && (
            <Empty description="No presets available" />
          )}
        </>
      )}
    </Modal>
  );
};

export default WidgetPresetsModal;
